import type { OnboardingProgress } from '@lipaflow/shared'

export const isAdminAssistedSetupPending = (
  progress: OnboardingProgress | null | undefined,
): boolean => Boolean(progress?.is_admin_setup && !progress.is_complete)

/**
 * Steps that wait on something outside the portal (LipaFlow connecting the
 * bank account, the bank reporting the first payment). Once setup reaches
 * one, the admin has done everything they can do in one sitting.
 */
const WAITING_STEPS = new Set(['set_paybill', 'go_live'])

/**
 * Whether the admin's own setup steps are done, so the rest of setup is
 * waiting on the bank and the portal need not be held back.
 */
export const isOwnSetupDone = (
  progress: OnboardingProgress | null | undefined,
): boolean => Boolean(
  progress &&
  (progress.is_complete || (progress.current_step && WAITING_STEPS.has(progress.current_step))),
)

/**
 * Whether to send a tenant admin to the setup wizard instead of the portal:
 * only while their own steps (details, phone sign-in) remain.
 */
export const shouldUseSelfServiceOnboarding = (
  progress: OnboardingProgress | null | undefined,
  canManageUsers: boolean,
): boolean => Boolean(
  progress &&
  !progress.is_complete &&
  !progress.is_admin_setup &&
  canManageUsers &&
  !isOwnSetupDone(progress),
)

/**
 * Checks whether Step 1 (organization / institution setup) is complete.
 * Once Step 1 is done, invited staff are no longer blocked from entering
 * the dashboard.
 */
export const isInitialSetupStepComplete = (
  progress: OnboardingProgress | null | undefined,
): boolean => {
  if (!progress) return false
  if (progress.is_complete) return true
  const completed = progress.completed_steps || {}
  if (completed['set_institution_details'] || completed['admin_create_institution']) {
    return true
  }
  const initialSteps = new Set(['accept_invite', 'set_institution_details', 'admin_create_institution'])
  return Boolean(progress.current_step && !initialSteps.has(progress.current_step))
}

/**
 * Determines whether a non-admin invited staff user should be blocked
 * by the pending setup splash screen.
 */
export const shouldBlockNonAdminStaff = (
  progress: OnboardingProgress | null | undefined,
  canManageUsers: boolean,
): boolean => {
  if (!progress || progress.is_complete) return false
  if (canManageUsers) return false
  if (progress.is_admin_setup) return true
  return !isInitialSetupStepComplete(progress)
}

/**
 * Identifies whether a tenant is running in demo mode.
 */
export const isDemoTenant = (
  tenant: { is_demo?: boolean; settings?: object } | null | undefined,
): boolean =>
  Boolean(tenant?.is_demo || (tenant?.settings as { is_demo?: unknown } | undefined)?.is_demo)
